import { useQuery } from '@tanstack/react-query';
import { createRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState, type ChangeEvent, type ReactNode } from 'react';

import { listCards, type CardTransaction } from '../api/client.ts';
import { IconChevronLeft, IconSearch } from '../components/icons.tsx';
import { InfiniteFooter } from '../components/infinite-footer.tsx';
import { ErrorState, TransactionListSkeleton } from '../components/states.tsx';
import {
  currencySymbol,
  declineNote,
  formatMagnitude,
  isCredit,
  movedMoney,
  readBilling,
  readFx,
  readMerchant,
  signedTotal,
  statusNote,
  txTypeLabel,
} from '../lib/transaction-view.ts';
import { useCardTransactions } from '../lib/use-card-transactions.ts';

import { rootRoute } from './__root.tsx';

const dateKey = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1)}-${String(d.getDate())}`;
};

const dateLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (d.toDateString() === today.toDateString()) return `Today · ${day}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday · ${day}`;
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const TxRow = ({ tx }: { readonly tx: CardTransaction }): ReactNode => {
  const status = statusNote(tx);
  // Nothing moved: the figure on this row is what was presented, not what was
  // taken, so it is struck through rather than dressed up as a real debit.
  const settled = movedMoney(tx);
  const credit = isCredit(tx);
  const billing = readBilling(tx);
  const fx = readFx(tx);
  const fxParts = fx !== null ? formatMagnitude(fx.amount) : null;
  const cls = !settled
    ? // A struck-through dash reads as a typo, so the two unsettled shapes get
      // their own class: a real figure is crossed out, an absent one is just dim.
      billing !== null
      ? 'unsettled'
      : 'absent'
    : status === 'Pending'
      ? 'pending'
      : credit
        ? 'credit'
        : 'debit';
  const sign = credit ? '+' : '−';
  // No card-currency figure (a declined foreign-currency attempt, never
  // priced): the row shows a dash rather than a dollar amount it cannot
  // stand behind. What was presented is on the merchant line below.
  const sym = billing !== null ? currencySymbol(billing.currency) : '';
  const parts = billing !== null ? formatMagnitude(billing.amount) : null;
  const desc = readMerchant(tx);
  const time = formatTime(tx.createdAt);
  const metaParts = [time, txTypeLabel(tx, credit), status ?? ''].filter((s) => s !== '');
  const reason = declineNote(tx);
  return (
    <div className="tx-row">
      <div className="tx-left">
        <p className="tx-desc">{desc}</p>
        <p className="tx-meta">{metaParts.join(' · ')}</p>
        {reason !== null ? <p className="tx-decline-reason">{reason}</p> : null}
      </div>
      <div className="tx-right">
        <p className={`tx-amount ${cls}`}>
          {parts !== null ? `${sign}${sym}${parts.int}${parts.dec}` : '—'}
        </p>
        {fx !== null && fxParts !== null ? (
          <p className={`tx-amount-sub${settled ? '' : ' unsettled'}`}>
            {currencySymbol(fx.currency)}
            {fxParts.int}
            {fxParts.dec}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export const TxnsPage = (): ReactNode => {
  const navigate = useNavigate();
  const { cardId } = useSearch({ from: transactionsRoute.id });
  const [search, setSearch] = useState<string>('');

  const cardsQ = useQuery({ queryKey: ['cards'], queryFn: ({ signal }) => listCards(signal) });

  // When cardId is provided (navigated from a specific card), show only that
  // card's transactions. Memoised because the paging hook keys its per-page
  // queries off this list.
  const cardIds = useMemo<readonly string[]>(() => {
    const all = cardsQ.data?.cards ?? [];
    return (cardId !== undefined ? all.filter((c) => c.id === cardId) : all).map((c) => c.id);
  }, [cardsQ.data?.cards, cardId]);

  const feed = useCardTransactions(cardIds);
  const allTxs = feed.transactions;

  const filtered = useMemo(() => {
    if (search === '') return allTxs;
    const q = search.toLowerCase();
    // Match on the merchant descriptor only. Substring-matching against the
    // raw amount string lets a user typing "-" or "12" pull every
    // negative transaction or every "12.xx" amount; surprising and
    // not what "search descriptions" implies.
    return allTxs.filter((tx) => readMerchant(tx).toLowerCase().includes(q));
  }, [allTxs, search]);

  const groups = useMemo(() => {
    const buckets = new Map<string, { readonly iso: string; readonly txs: CardTransaction[] }>();
    const order: string[] = [];
    for (const tx of filtered) {
      const key = dateKey(tx.createdAt);
      const existing = buckets.get(key);
      if (existing === undefined) {
        buckets.set(key, { iso: tx.createdAt, txs: [tx] });
        order.push(key);
      } else {
        existing.txs.push(tx);
      }
    }
    return order.map((k) => {
      const bucket = buckets.get(k) as { iso: string; txs: CardTransaction[] };
      return { key: k, iso: bucket.iso, txs: bucket.txs, total: signedTotal(bucket.txs) };
    });
  }, [filtered]);

  // Searching only reaches the pages already pulled down, so an empty result
  // must not read as "this merchant is not in your history".
  const emptyCopy =
    search === ''
      ? 'No activity yet'
      : feed.hasMore
        ? `No matches for "${search}" in the transactions loaded so far`
        : `No matches for "${search}"`;

  // The tail is meaningless before anything has loaded and while the whole
  // screen is an error; otherwise it always shows, including under an empty
  // search result so the user can keep pulling history in.
  const showFooter =
    !feed.isLoadingFirstPage &&
    !(feed.isFirstPageError && allTxs.length === 0) &&
    (allTxs.length > 0 || feed.hasMore || feed.isPageError);

  if (cardsQ.isLoading)
    return (
      <main className="screen">
        <div className="topbar fade-in">
          <button
            type="button"
            className="topbar-back"
            aria-label="Back"
            onClick={(): void => {
              void navigate({ to: '/', search: cardId !== undefined ? { cardId } : {} });
            }}
          >
            <IconChevronLeft />
          </button>
          <h1 className="topbar-title topbar-title--quiet">Activity</h1>
          <span className="topbar-spacer" aria-hidden="true" />
        </div>
        <TransactionListSkeleton rows={8} />
      </main>
    );
  if (cardsQ.isError) {
    return (
      <ErrorState
        message="Could not load activity"
        onRetry={(): void => {
          void cardsQ.refetch();
        }}
      />
    );
  }

  return (
    <main className="screen">
      <div className="topbar fade-in">
        <button
          type="button"
          className="topbar-back"
          aria-label="Back"
          onClick={(): void => {
            void navigate({ to: '/', search: cardId !== undefined ? { cardId } : {} });
          }}
        >
          <IconChevronLeft />
        </button>
        <h1 className="topbar-title topbar-title--quiet">Activity</h1>
        <span className="topbar-spacer" aria-hidden="true" />
      </div>

      <div className="search fade-in d1">
        <span className="search-icon" aria-hidden="true">
          <IconSearch />
        </span>
        <input
          type="text"
          className="search-input"
          placeholder="Search descriptions"
          autoComplete="off"
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setSearch(e.target.value);
          }}
        />
      </div>

      <div className="fade-in d2">
        {feed.isLoadingFirstPage ? (
          <TransactionListSkeleton rows={6} />
        ) : feed.isFirstPageError && allTxs.length === 0 ? (
          <ErrorState message="Could not load activity" onRetry={feed.retry} />
        ) : filtered.length === 0 ? (
          <div className="empty-state">{emptyCopy}</div>
        ) : (
          groups.map((g) => {
            const sign = g.total >= 0 ? '+' : '−';
            const sym = currencySymbol(g.txs[0]?.currency);
            const totalAbs = formatMagnitude(g.total);
            return (
              <section key={g.key}>
                <div className="tx-date-header">
                  <p className="tx-date-label">{dateLabel(g.iso)}</p>
                  <p className="tx-date-total">
                    {sign}
                    {sym}
                    {totalAbs.int}
                    {totalAbs.dec}
                  </p>
                </div>
                {g.txs.map((tx, i) => (
                  <div key={tx.id}>
                    <TxRow tx={tx} />
                    {i < g.txs.length - 1 ? <div className="hairline" /> : null}
                  </div>
                ))}
              </section>
            );
          })
        )}

        {showFooter ? (
          <InfiniteFooter
            hasMore={feed.hasMore}
            isLoading={feed.isLoadingMore}
            isError={feed.isPageError}
            onLoadMore={feed.loadMore}
            onRetry={feed.retry}
            autoLoad={search === ''}
            endLabel="No older transactions"
            loadMoreLabel={search === '' ? 'Load older transactions' : 'Search older transactions'}
          />
        ) : null}
      </div>
    </main>
  );
};

export interface TransactionsSearch {
  readonly cardId?: string;
}

export const transactionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transactions',
  component: TxnsPage,
  validateSearch: (s: Record<string, unknown>): TransactionsSearch =>
    typeof s.cardId === 'string' ? { cardId: s.cardId } : {},
});
